import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import { apiRequest } from "../../api/http";
import { Faq, FaqCategory, PaginatedResponse } from "../../types/admin";

export type FaqListQuery = {
  q?: string;
  category?: FaqCategory;
  is_visible?: boolean;
  page?: number;
  page_size?: number;
};

type FaqPayload = {
  question: string;
  answer: string;
  category?: FaqCategory | null;
  sort_order?: number;
  is_visible?: boolean;
};

type FaqsState = {
  list: Faq[];
  totalCount: number;
  page: number;
  pageSize: number;
  loadingList: boolean;
  listError: string | null;
};

const initialState: FaqsState = {
  list: [],
  totalCount: 0,
  page: 1,
  pageSize: 20,
  loadingList: false,
  listError: null,
};

const toQuery = (query: FaqListQuery): string => {
  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      params.set(key, String(value));
    }
  });
  const queryString = params.toString();
  return queryString ? `?${queryString}` : "";
};

export const fetchFaqs = createAsyncThunk<PaginatedResponse<Faq>, FaqListQuery, { rejectValue: string }>(
  "faqs/fetchFaqs",
  async (query, { rejectWithValue }) => {
    try {
      return await apiRequest<PaginatedResponse<Faq>>(`/admin/faqs${toQuery(query)}`);
    } catch (error) {
      const message = error instanceof Error ? error.message : "FAQ 목록 조회에 실패했습니다.";
      return rejectWithValue(message);
    }
  }
);

export const createFaq = createAsyncThunk<Faq, FaqPayload, { rejectValue: string }>(
  "faqs/createFaq",
  async (payload, { rejectWithValue }) => {
    try {
      return await apiRequest<Faq>("/admin/faqs", { method: "POST", body: payload });
    } catch (error) {
      const message = error instanceof Error ? error.message : "FAQ 등록에 실패했습니다.";
      return rejectWithValue(message);
    }
  }
);

export const updateFaq = createAsyncThunk<
  Faq,
  { faqId: number; payload: Partial<FaqPayload> },
  { rejectValue: string }
>("faqs/updateFaq", async ({ faqId, payload }, { rejectWithValue }) => {
  try {
    return await apiRequest<Faq>(`/admin/faqs/${faqId}`, {
      method: "PATCH",
      body: payload,
    });
  } catch (error) {
    const message = error instanceof Error ? error.message : "FAQ 수정에 실패했습니다.";
    return rejectWithValue(message);
  }
});

export const deleteFaq = createAsyncThunk<number, number, { rejectValue: string }>(
  "faqs/deleteFaq",
  async (faqId, { rejectWithValue }) => {
    try {
      await apiRequest<{ deleted: boolean; faq_id: number }>(`/admin/faqs/${faqId}`, { method: "DELETE" });
      return faqId;
    } catch (error) {
      const message = error instanceof Error ? error.message : "FAQ 삭제에 실패했습니다.";
      return rejectWithValue(message);
    }
  }
);

const faqsSlice = createSlice({
  name: "faqs",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchFaqs.pending, (state) => {
        state.loadingList = true;
        state.listError = null;
      })
      .addCase(fetchFaqs.fulfilled, (state, action) => {
        state.loadingList = false;
        state.list = action.payload.items;
        state.totalCount = action.payload.total_count;
        state.page = action.payload.page;
        state.pageSize = action.payload.page_size;
      })
      .addCase(fetchFaqs.rejected, (state, action) => {
        state.loadingList = false;
        state.listError = action.payload ?? "FAQ 목록 조회에 실패했습니다.";
      })
      .addCase(createFaq.fulfilled, (state, action) => {
        state.list = [action.payload, ...state.list];
      })
      .addCase(updateFaq.fulfilled, (state, action) => {
        state.list = state.list.map((faq) => (faq.faq_id === action.payload.faq_id ? action.payload : faq));
      })
      .addCase(deleteFaq.fulfilled, (state, action) => {
        state.list = state.list.filter((faq) => faq.faq_id !== action.payload);
      });
  },
});

export default faqsSlice.reducer;
